import { useState } from "react";

import {
  createApiToken,
  destroyApiToken,
  getApiToken,
} from "@/api/endpoints/api-token";
import ClipboardDocumentIcon from "@/assets/icons/clipboard-document.svg";
import EyeIcon from "@/assets/icons/eye.svg";
import EyeOffIcon from "@/assets/icons/eye-off.svg";
import KeyIcon from "@/assets/icons/key.svg";
import TrashIcon from "@/assets/icons/trash.svg";
import { useMessages } from "@/containers/Messages/MessageContext";
import { cx } from "@/utils";
import Button from "@components/Button";
import Typography from "@components/Typography";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import styles from "./ApiToken.module.css";

interface ApiTokenProps {
  className?: string;
}

const ApiToken = ({ className }: ApiTokenProps) => {
  const queryClient = useQueryClient();
  const { success, danger } = useMessages();

  const [isShowingAPIKey, setIsShowingAPIKey] = useState<boolean>(false);

  // Query to fetch the API token
  const { data: tokenResponse, isLoading: isLoadingToken } = useQuery({
    queryKey: ["api-token"],
    queryFn: getApiToken,
  });

  // Mutation to create a new API token
  const { mutate: createToken, isPending: isCreatingToken } = useMutation({
    mutationFn: createApiToken,
    onSuccess: (res) => {
      if (res.ok) {
        success({ title: L.API_TOKEN_CREATED });
        queryClient.setQueryData(["api-token"], res);
        setIsShowingAPIKey(true);
      } else {
        danger({ title: L.API_TOKEN_ERROR_CREATING });
      }
    },
    onError: () => {
      danger({ title: L.API_TOKEN_ERROR_CREATING });
    },
  });

  // Mutation to delete the API token
  const { mutate: deleteToken, isPending: isDeletingToken } = useMutation({
    mutationFn: destroyApiToken,
    onSuccess: (res) => {
      if (res.ok) {
        success({ title: L.API_TOKEN_DELETED });
        queryClient.removeQueries({ queryKey: ["api-token"] });
      } else {
        danger({ title: L.API_TOKEN_ERROR_DELETING });
      }
    },
    onError: () => {
      danger({ title: L.API_TOKEN_ERROR_DELETING });
    },
  });

  const handleToggleShowToken = () => {
    setIsShowingAPIKey(!isShowingAPIKey);
  };

  const handleCreateToken = () => {
    createToken();
  };

  const handleDeleteToken = () => {
    if (window.confirm(L.API_TOKEN_CONFIRM_DELETE)) {
      deleteToken();
    }
  };

  const isLoading = isLoadingToken || isCreatingToken || isDeletingToken;
  const token = tokenResponse?.ok && tokenResponse?.data?.token;

  const handleCopyToken = async () => {
    if (token) {
      try {
        await navigator.clipboard.writeText(token);
        success({ title: L.API_TOKEN_COPIED });
      } catch {
        danger({ title: L.API_TOKEN_ERROR_COPYING });
      }
    }
  };

  return (
    <div className={cx(styles.container, className)}>
      <Typography margin="none">{L.API_TOKEN_DESCRIPTION}</Typography>

      {token ? (
        <>
          {isShowingAPIKey && (
            <div className={styles.tokenContainer}>
              <Typography
                margin="none"
                variant="monospace"
                className={styles.tokenText}
              >
                {token}
              </Typography>
              <Button
                onClick={handleCopyToken}
                icon={ClipboardDocumentIcon}
                variant="tertiary"
                size="sm"
                data-testid="copy-api-token"
              />
            </div>
          )}

          <div className={styles.buttonContainer}>
            <Button
              onClick={handleToggleShowToken}
              icon={isShowingAPIKey ? EyeOffIcon : EyeIcon}
              variant="secondary"
              disabled={isLoading}
              data-testid="view-api-token"
            >
              {isShowingAPIKey ? L.API_TOKEN_HIDE : L.API_TOKEN_VIEW}
            </Button>

            <Button
              onClick={handleDeleteToken}
              disabled={isLoading}
              data-testid="delete-api-token"
              icon={TrashIcon}
            >
              {L.API_TOKEN_DELETE}
            </Button>
          </div>
        </>
      ) : (
        <Button
          onClick={handleCreateToken}
          variant="primary"
          disabled={isLoading}
          data-testid="create-api-token"
          icon={KeyIcon}
        >
          {L.API_TOKEN_CREATE}
        </Button>
      )}
    </div>
  );
};

export default ApiToken;
